import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { adjustByGreekPrefix } from 'core/util';
import {
  InternalFilters,
  InternalFilterGroups,
  InboundFilters,
  InboundFilterGroups,
  OutboundFilters,
  OutboundFilterGroups,
  ThroughFilters,
  ThroughFilterGroups
} from 'app/stores/query/baseQueries/inboundOutboundInternalQuery';
import {
  InboundFilters as MkpInboundFilters,
  InboundFilterGroups as MkpInboundFilterGroups,
  OutboundFilters as MkpOutboundFilters,
  OutboundFilterGroups as MkpOutboundFilterGroups
} from 'app/stores/query/baseQueries/mkp/inboundOutboundQuery';
import { getAggregateUnitLabel } from 'app/util/aggregateLabels';
import { zeroToText } from 'app/util/utils';

import Tab from './Tab';

@inject('$app')
@observer
class Tabs extends Component {
  state = {
    tabName: null,
    lastChartSummary: null // Track last sent summary to prevent infinite loops
  };

  constructor(props) {
    super(props);

    if (props.$app.isSubtenant) {
      this.tabFilters = {
        Inbound: { ...MkpInboundFilters, ...MkpInboundFilterGroups },
        Outbound: { ...MkpOutboundFilters, ...MkpOutboundFilterGroups }
      };
    } else {
      this.tabFilters = {
        Internal: { ...InternalFilters, ...InternalFilterGroups },
        Inbound: { ...InboundFilters, ...InboundFilterGroups },
        Outbound: { ...OutboundFilters, ...OutboundFilterGroups },
        Through: { ...ThroughFilters, ...ThroughFilterGroups }
      };
    }
  }

  handleTabChange = (tabName) => {
    const { onTabChange } = this.props;
    this.setState({ tabName }, () => onTabChange(tabName));
  };

  componentDidUpdate(prevProps) {
    // Notify parent when data changes so it can update AI context
    const { data, onDataUpdate, queryResults, queryModel } = this.props;

    if (!onDataUpdate || !data || !queryResults) {
      return;
    }

    // Only update if data actually changed (check if it's different object AND has different content)
    if (data === prevProps.data) {
      return; // Same object reference, no change
    }

    // Extract current values
    const chartSummary = {};
    const outsortField = queryModel.get('outsort');

    data.forEach((series) => {
      const value = series[outsortField] || null;
      if (value !== null && series.key) {
        const tabKey = series.key.toLowerCase();
        chartSummary[tabKey] = value;
      }
    });

    // Only call onDataUpdate if values actually changed (prevent infinite loop)
    const { lastChartSummary } = this.state;

    // Shallow comparison: check if keys and values changed
    let valuesChanged = !lastChartSummary;
    if (lastChartSummary) {
      const newKeys = Object.keys(chartSummary);
      const oldKeys = Object.keys(lastChartSummary);

      // Check if number of keys changed or any value changed
      if (newKeys.length !== oldKeys.length) {
        valuesChanged = true;
      } else {
        valuesChanged = newKeys.some((key) => chartSummary[key] !== lastChartSummary[key]);
      }
    }

    if (Object.keys(chartSummary).length > 0 && valuesChanged) {
      this.setState({ lastChartSummary: chartSummary });
      onDataUpdate(chartSummary);
    }
  }

  render() {
    const { data, lookback_seconds, queryModel, queryResults, aggregatePrefix, viewProps, noTrend } = this.props;
    const { tabName } = this.state;

    if (!queryResults) {
      return null;
    }

    const { prefix } = queryResults;
    const [aggregate] = queryModel.aggregates;

    const overlays = data.filter((series) => series.isOverlay);
    const filterDimensions = queryModel.get('filterDimensions.filterGroups');
    const filterDimensionSeries = filterDimensions.map((dimension) => {
      const row = data.find((series) => series.key.toLowerCase() === dimension.name.toLowerCase());

      if (!row) {
        return {
          key: dimension.name,
          name: dimension.name,
          title: dimension.name
        };
      }
      // mimic FBD rows
      row.key = dimension.name;
      row.name = dimension.name;
      row.title = dimension.name;

      return row;
    });

    return overlays.concat(filterDimensionSeries).map((series, index) => {
      let value = series[queryModel.get('outsort')] || null;
      if (value) {
        value = zeroToText(adjustByGreekPrefix(value, aggregatePrefix[queryModel.outsortUnit]), {
          fix: 0
        });
      }
      return (
        <Tab
          index={index}
          key={series.key}
          name={series.name}
          title={series.name}
          filters={this.tabFilters[series.name]}
          lookback_seconds={lookback_seconds}
          series={series}
          value={value}
          unit={getAggregateUnitLabel({ aggregate, prefix })}
          viewProps={viewProps}
          onTabChange={this.handleTabChange}
          active={tabName === series.name || (!tabName && index === 0)}
          noTrend={noTrend}
        />
      );
    });
  }
}

export default Tabs;
